// Paginated "More in the news" list.
// Progressive enhancement: the server renders real ?page=N anchors (crawlable,
// work without JS). This element intercepts clicks and swaps the article list
// in place with a fade — no page reload, no scroll jump.
//
// Every page renders the same number of cards (per_page), so heights match. As
// a safety net (the last page, or a page that skips an excluded pick, can be
// shorter), the list reserves the tallest full-page height so it never
// collapses — which is what was throwing the scroll position around.
//
// Pages are fetched via Shopify's Section Rendering API (just this section,
// ~26KB) instead of the whole blog page (~200KB), with a full-page fetch as a
// fallback, and a normal navigation as the final fallback.
class ArticlePaginatedList extends HTMLElement {
  connectedCallback() {
    this.list = this.querySelector("[data-more-news-list]");
    this.baseUrl = this.getAttribute("data-base-url") || window.location.pathname;
    this.perPage = parseInt(this.getAttribute("data-per-page") || "0", 10);
    const wrapper = this.closest('[id^="shopify-section-"]');
    this.sectionId = wrapper ? wrapper.id.replace("shopify-section-", "") : null;
    this.cache = {};
    this.maxFull = 0;
    this.busy = false;
    if (!this.list) return;

    // Cache the page the server already rendered.
    const current = parseInt(this.getAttribute("data-current") || "1", 10);
    const nav = this.querySelector("[data-pagination]");
    this.cache[current] = { list: this.list.innerHTML, nav: nav ? nav.innerHTML : "" };
    this.reserveHeight();

    this.addEventListener("click", (e) => {
      const link = e.target.closest("a[data-page]");
      if (!link) return;
      const page = parseInt(link.getAttribute("data-page"), 10);
      if (!page) return;
      e.preventDefault();
      if (this.busy || String(page) === this.getAttribute("data-current")) return;
      this.goTo(page, link.href, true);
    });

    window.addEventListener("popstate", () => {
      const params = new URLSearchParams(window.location.search);
      const page = parseInt(params.get("page") || "1", 10);
      if (String(page) === this.getAttribute("data-current")) return;
      this.goTo(page, this.baseUrl + (page > 1 ? "?page=" + page : ""), false);
    });

    // Re-measure on resize (card heights are responsive).
    window.addEventListener("resize", () => {
      this.maxFull = 0;
      this.reserveHeight();
    });
  }

  // Reserve the tallest full-page height so shorter pages never shrink the box.
  reserveHeight() {
    this.list.style.minHeight = "";
    const count = this.list.querySelectorAll("a[href]").length;
    if (this.perPage && count >= this.perPage) {
      const h = this.list.offsetHeight;
      if (h > this.maxFull) this.maxFull = h;
    }
    if (this.maxFull) this.list.style.minHeight = this.maxFull + "px";
  }

  async goTo(page, href, pushUrl) {
    this.busy = true;
    try {
      const data = this.cache[page] || (await this.fetchPage(page, href));
      this.cache[page] = data;
      await this.swap(data);
      this.setAttribute("data-current", String(page));
      if (pushUrl) history.pushState({ page }, "", href);
    } catch (err) {
      // Anything unexpected: fall back to a normal page load.
      window.location.href = href;
      return;
    }
    this.busy = false;
  }

  async fetchPage(page, href) {
    let html;
    // Preferred: just this section (much smaller payload).
    if (this.sectionId) {
      try {
        const res = await fetch(this.baseUrl + "?section_id=" + encodeURIComponent(this.sectionId) + "&page=" + page);
        if (res.ok) html = await res.text();
      } catch (e) { /* fall through to full-page fetch */ }
    }
    // Fallback: the full page the link points to.
    if (!html) {
      const res = await fetch(href);
      if (!res.ok) throw new Error("Request failed");
      html = await res.text();
    }
    const doc = new DOMParser().parseFromString(html, "text/html");
    const newList = doc.querySelector("[data-more-news-list]");
    if (!newList) throw new Error("List not found in response");
    const newNav = doc.querySelector("[data-pagination]");
    return { list: newList.innerHTML, nav: newNav ? newNav.innerHTML : "" };
  }

  swap(data) {
    return new Promise((resolve) => {
      const nav = this.querySelector("[data-pagination]");
      this.list.style.opacity = "0";
      setTimeout(() => {
        this.list.innerHTML = data.list;
        if (nav) nav.innerHTML = data.nav;
        this.reserveHeight();
        void this.list.offsetWidth; // force reflow so the fade-in animates
        this.list.style.opacity = "1";
        setTimeout(resolve, 200);
      }, 200);
    });
  }
}

customElements.define("article-paginated-list", ArticlePaginatedList);
